// src/pages/om/WorkOrderPage.js
// 运维中心 · 工单（doc01 2.15，缺陷 99/100/101/102）
// 结构：头部「工单」+ 右上「新建工单」 → 4 个状态 Tab（带数量角标）→ 查询区（电站/工单类型/工单来源 + 查询/重置）
//      → 表格（电站名称/工单类型/工单来源/创建时间/期望处理时间/操作）→ 分页(10/页)。
// 操作：详情（二级弹层：工单详情 + 进度流程 + 执行详情）/ 编辑 / 删除；新建/查询/重置均真实可用（本地增删改 + message）。
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table, Select, Button, Space, Modal, Form, Input, Popconfirm, Tag, Badge, message,
} from 'antd';
import dayjs from 'dayjs';
import PageSkeleton from '../../components/PageSkeleton';
import ScrollTabs from '../../components/ScrollTabs';
import { findMenu, pick } from '../../config/menuConfig';
import { STATIONS, WORK_ORDERS, WO_PROGRESS } from '../../config/stationData';

const STATES = ['待接单', '待执行', '已完成', '已关闭'];
const TYPES = ['故障检修', '巡视检查', '维护保养'];
// 缺陷 99：来源枚举与数据取值保持一致（故障转工单 / APP上报 / 计划 / 手动创建）
const SOURCES = ['故障转工单', 'APP上报', '计划', '手动创建'];
const LEVELS = ['紧急', '高', '中', '低'];

const TYPE_COLOR = { 故障检修: 'red', 巡视检查: 'blue', 维护保养: 'cyan' };
const SOURCE_COLOR = { 故障转工单: 'red', APP上报: 'blue', 计划: 'cyan', 手动创建: 'default' };

const nowStr = () => dayjs().format('YYYY-MM-DD HH:mm');

const KV = ({ items }) => (
  <div className="dev-kv">
    {items.map(([k, v]) => (
      <div className="dev-kv-row" key={k}>
        <span className="dev-kv-k">{k}</span>
        <span className="dev-kv-v">{v == null || v === '' ? '-' : v}</span>
      </div>
    ))}
  </div>
);

// 待接单/待执行：无期望处理时间者（多为故障检修）按创建时间倒序在前，有期望处理时间者按开始时间倒序；完成/关闭按完成时间倒序
const sortRows = (state, rows) => [...rows].sort((a, b) => {
  if (state === '已完成' || state === '已关闭') return String(b.finish || '').localeCompare(String(a.finish || ''));
  const ea = a.expect ? 1 : 0;
  const eb = b.expect ? 1 : 0;
  if (ea !== eb) return ea - eb;
  if (!ea) return String(b.created || '').localeCompare(String(a.created || ''));
  return String(b.expect || '').localeCompare(String(a.expect || ''));
});

const WorkOrderPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const meta = findMenu('/app/om/workorder');

  const [list, setList] = useState(() => WORK_ORDERS.map((w) => ({ ...w })));
  const [tab, setTab] = useState('待接单');
  const [draft, setDraft] = useState({});     // 查询区待提交值
  const [applied, setApplied] = useState({}); // 已提交的筛选值
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const stationOptions = useMemo(() => Array.from(new Set([
    ...STATIONS.map((s) => s.name),
    ...list.map((w) => w.station).filter(Boolean),
  ])), [list]);

  const countOf = (s) => list.filter((w) => w.state === s).length;

  const matchFilters = (w) => (
    (!applied.station || w.station === applied.station)
    && (!applied.type || w.type === applied.type)
    && (!applied.source || w.source === applied.source)
  );

  const columns = [
    { title: '电站名称', dataIndex: 'station', width: 160 },
    { title: '工单类型', dataIndex: 'type', width: 110, render: (v) => <Tag color={TYPE_COLOR[v] || 'default'} style={{ margin: 0 }}>{v}</Tag> },
    { title: '工单来源', dataIndex: 'source', width: 120, render: (v) => <Tag color={SOURCE_COLOR[v] || 'default'} style={{ margin: 0 }}>{v}</Tag> },
    { title: '创建时间', dataIndex: 'created', width: 160 },
    { title: '期望处理时间', dataIndex: 'expect', width: 160, render: (v) => v || <span style={{ color: 'rgba(255,255,255,0.3)' }}>-</span> },
    {
      title: '操作', key: 'op', width: 190, fixed: 'right',
      render: (_, r) => (
        <Space size={2}>
          <Button type="link" size="small" onClick={() => setDetail(r)}>详情</Button>
          <Button type="link" size="small" onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm
            title="删除工单"
            description={`确认删除工单「${r.no}」？删除后不可恢复。`}
            okText="删除" cancelText="取消" okButtonProps={{ danger: true }}
            onConfirm={() => removeRow(r)}
          >
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ type: '故障检修', source: '手动创建', level: '中', station: STATIONS[0]?.name, expect: nowStr() });
    setCreating(true);
  };

  function openEdit(r) {
    setEditing(r);
    form.setFieldsValue({
      title: r.title, type: r.type, source: r.source, level: r.level,
      station: r.station, assignee: r.assignee, expect: r.expect, note: r.note,
    });
    setCreating(true);
  }

  const submitForm = async () => {
    const v = await form.validateFields();
    if (editing) {
      setList((prev) => prev.map((w) => (w.key === editing.key ? { ...w, ...v } : w)));
      message.success('工单已更新');
    } else {
      const key = Date.now();
      const no = `WO-${dayjs().format('YYYYMMDD')}-${String(list.length + 1).padStart(3, '0')}`;
      setList((prev) => [{
        key, no, title: v.title, type: v.type, source: v.source, level: v.level,
        station: v.station, assignee: v.assignee || '待分配', created: nowStr(),
        expect: v.expect || '', finish: '', state: '待接单', note: v.note,
      }, ...prev]);
      setTab('待接单');
      message.success('工单已创建');
    }
    setCreating(false);
    form.resetFields();
    setEditing(null);
  };

  const removeRow = (r) => {
    setList((prev) => prev.filter((w) => w.key !== r.key));
    message.success('工单已删除');
  };

  const doQuery = () => { setApplied({ ...draft }); message.success('已按当前筛选条件查询'); };
  const doReset = () => { setDraft({}); setApplied({}); message.success('筛选条件已重置'); };

  const actions = [{ key: 'new', label: '新建工单', primary: true }];
  const onAction = (a) => { if (a.key === 'new') openCreate(); };

  const queryBar = (
    <div className="filter-bar">
      <div className="filter-bar-fields">
        <div className="filter-item">
          <span className="filter-label">电站</span>
          <Select allowClear placeholder="全部" style={{ width: 180 }} value={draft.station}
            options={stationOptions.map((n) => ({ value: n, label: n }))}
            onChange={(v) => setDraft((s) => ({ ...s, station: v }))} />
        </div>
        <div className="filter-item">
          <span className="filter-label">工单类型</span>
          <Select allowClear placeholder="全部" style={{ width: 140 }} value={draft.type}
            options={TYPES.map((v) => ({ value: v, label: v }))}
            onChange={(v) => setDraft((s) => ({ ...s, type: v }))} />
        </div>
        <div className="filter-item">
          <span className="filter-label">工单来源</span>
          <Select allowClear placeholder="全部" style={{ width: 150 }} value={draft.source}
            options={SOURCES.map((v) => ({ value: v, label: v }))}
            onChange={(v) => setDraft((s) => ({ ...s, source: v }))} />
        </div>
      </div>
      <div className="filter-bar-actions">
        <Space>
          <Button type="primary" onClick={doQuery}>查询</Button>
          <Button onClick={doReset}>重置</Button>
        </Space>
      </div>
    </div>
  );

  const tabItems = STATES.map((s) => ({
    key: s,
    label: <span>{s}<Badge count={countOf(s)} size="small" overflowCount={999} style={{ marginLeft: 6 }} /></span>,
    children: (
      <>
        {queryBar}
        <Table
          rowKey="key"
          dataSource={sortRows(s, list.filter((w) => w.state === s && matchFilters(w)))}
          columns={columns}
          pagination={{ pageSize: 10, size: 'small' }}
          size="middle"
          scroll={{ x: 960 }}
        />
      </>
    ),
  }));

  const d = detail || {};
  const progressColumns = [
    { title: '节点', dataIndex: 'node', width: 110 },
    { title: '操作人', dataIndex: 'operator', width: 100 },
    { title: '时间', dataIndex: 'time', width: 160 },
    { title: '备注', dataIndex: 'remark' },
  ];

  return (
    <>
      <PageSkeleton
        title={meta ? pick(meta.label, lang) : '工单'}
        desc={meta ? pick(meta.desc, lang) : ''}
        crumb={meta ? [pick(meta.groupLabel, lang), pick(meta.sectionLabel, lang), pick(meta.label, lang)] : []}
        filters={[]}
        actions={actions}
        onAction={onAction}
      >
        <ScrollTabs activeKey={tab} onChange={setTab} items={tabItems} />
      </PageSkeleton>

      {/* 详情：二级弹层，含「工单详情 + 进度流程 + 执行详情」三块（缺陷 101） */}
      <Modal
        open={!!detail}
        title={`工单详情 · ${d.no || ''}`}
        width={880}
        onCancel={() => setDetail(null)}
        footer={<Button type="primary" onClick={() => setDetail(null)}>关闭</Button>}
      >
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-title">工单详情</div>
          <KV items={[
            ['工单号', d.no], ['标题', d.title], ['工单类型', d.type], ['工单来源', d.source],
            ['级别', d.level], ['所属站点', d.station], ['负责人', d.assignee],
            ['创建时间', d.created], ['期望处理时间', d.expect], ['状态', d.state],
            ['处理说明', d.note || '—'],
          ]} />
        </div>
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-title">进度流程<small>按时间正序</small></div>
          <Table rowKey="key" size="small" pagination={false} dataSource={WO_PROGRESS} columns={progressColumns} />
        </div>
        <div className="panel">
          <div className="panel-title">执行详情</div>
          <KV items={[
            ['执行人', d.assignee],
            ['现场开始时间', (WO_PROGRESS.find((p) => p.node === '现场执行') || {}).time],
            ['处理时长', d.finish ? '—' : '处理中'],
            ['处理结果', d.state === '已完成' ? '已复归' : d.state === '已关闭' ? '已关闭' : '处理中'],
            ['完成时间', d.finish || '—'],
            ['现场照片', d.state === '已完成' || d.state === '已关闭' ? '2 张（现场照片）' : '未上传'],
            ['处理说明', (WO_PROGRESS.find((p) => p.node === '现场执行') || {}).remark],
          ]} />
        </div>
      </Modal>

      {/* 新建 / 编辑：弹窗表单，提交后真实写入列表 */}
      <Modal
        open={creating}
        title={editing ? '编辑工单' : '新建工单'}
        onCancel={() => { setCreating(false); setEditing(null); form.resetFields(); }}
        onOk={submitForm}
        okText={editing ? '保存' : '创建'}
        cancelText="取消"
        width={640}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="工单标题" rules={[{ required: true, message: '请输入工单标题' }]}>
            <Input placeholder="请输入工单标题" maxLength={60} />
          </Form.Item>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="type" label="工单类型" rules={[{ required: true, message: '请选择工单类型' }]} style={{ width: 190 }}>
              <Select options={TYPES.map((v) => ({ value: v, label: v }))} />
            </Form.Item>
            <Form.Item name="source" label="工单来源" rules={[{ required: true, message: '请选择工单来源' }]} style={{ width: 190 }}>
              <Select options={SOURCES.map((v) => ({ value: v, label: v }))} />
            </Form.Item>
            <Form.Item name="level" label="级别" rules={[{ required: true, message: '请选择级别' }]} style={{ width: 150 }}>
              <Select options={LEVELS.map((v) => ({ value: v, label: v }))} />
            </Form.Item>
          </Space>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="station" label="所属站点" rules={[{ required: true, message: '请选择所属站点' }]} style={{ width: 260 }}>
              <Select showSearch options={stationOptions.map((n) => ({ value: n, label: n }))} />
            </Form.Item>
            <Form.Item name="assignee" label="负责人" style={{ width: 200 }}>
              <Input placeholder="待分配" />
            </Form.Item>
          </Space>
          <Form.Item name="expect" label="期望处理时间" tooltip="计划 / 手动创建工单时设置，格式 YYYY-MM-DD HH:mm">
            <Input placeholder="YYYY-MM-DD HH:mm" />
          </Form.Item>
          <Form.Item name="note" label="处理说明（≤ 200 字）">
            <Input.TextArea rows={3} maxLength={200} showCount placeholder="请填写故障现象 / 处理要求" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default WorkOrderPage;
